/**
 * Domain value objects for user permissions
 * Contains all permission constants for the users domain based on existing permissions in the codebase
 *
 * @module domains/users/domain/value-objects/permissions
 */

/**
 * Organization-level permissions for user management
 * @readonly
 */
export const ORGANIZATION_PERMISSIONS = {
  /** Permission to access the Admin Portal at Organization level */
  ACCESS: 'organization.access',
  /** Permission to view users and roles at organization level */
  DISPLAY_USERS_AND_ROLES: 'organization.displayUsersAndRoles',
  /** Permission to edit users and roles at organization level */
  EDIT_USERS_AND_ROLES: 'organization.editUsersAndRoles',
} as const;

/**
 * Admin permissions for user management
 * @readonly
 */
export const ADMIN_PERMISSIONS = {
  /** Gift certificate administration */
  CAN_ADMIN_GIFT_CERTIFICATE: 'admin.canAdminGiftCerticate',
  /** Modify employees and permissions */
  MODIFY_EMPLOYEES_AND_PERMISSIONS: 'admin.modifyEmployeesAndPermissions',
  /** Full admin access to store */
  STORE: 'admin.store',
  /** Ticket editor access - Permite el acceso a el editor de tickets */
  TICKET_EDITOR: 'invoicing.ticketEditor',
  /** Type products editor */
  TYPE_PRODUCTS_EDITOR: 'admin.typeProductsEditor',
} as const;

/**
 * Notification permissions
 * @readonly
 */
export const NOTIFICATION_PERMISSIONS = {
  /** Permission to close cash session notifications */
  CLOSE_CASH_SESSION: 'notification.closeCashSession',
  /** Permission to subscription plan notifications */
  SUBSCRIPTION_PLAN: 'notification.subscriptionPlan',
  /** Permission to pay deposits notifications */
  PAY_DEPOSITS: 'notification.payDeposits',
} as const;

/**
 * Back office permissions
 * @readonly
 */
export const BACK_OFFICE_PERMISSIONS = {
  /** Inventory Organization module access */
  MODULE_INVENTORY_ORGANIZATION: 'backoffice.moduleInventoryOrganization',
  /** Invoicing module access */
  MODULE_INVOICING: 'backoffice.moduleInvoicing',
  /** Menu module access */
  MODULE_MENU: 'backoffice.moduleMenu',
  /** Reports module access */
  MODULE_REPORTS: 'backoffice.moduleReports',
  /** Settings module access */
  MODULE_SETTINGS: 'backoffice.moduleSettings',
  /** Subscriptions module access */
  MODULE_SUBSCRIPTIONS: 'backoffice.moduleSubscriptions',
  /** Workforce module access */
  MODULE_WORKFORCE: 'backoffice.moduleWorkforce',
  /** Pay access in back office */
  PAY_ACCESS: 'backoffice.payAccess',
} as const;

/**
 * Pay terminal permissions
 * @readonly
 */
export const PAY_TERMINAL_PERMISSIONS = {
  /** View all transactions permission */
  CAN_VIEW_ALL_TRANSACTIONS: 'payTerminal.canViewAllTransactions',
  /** Login access to pay terminal */
  LOGIN_ACCESS: 'payTerminal.loginAccess',
} as const;

/**
 * Invoicing permissions
 * @readonly
 */
export const INVOICING_PERMISSIONS = {
  // Note: TICKET_EDITOR moved to ADMIN_PERMISSIONS to match API namespace
} as const;

/**
 * Area tables permissions for table management
 * @readonly
 */
export const AREA_TABLES_PERMISSIONS = {
  /** Change table assigned to order account */
  CAN_CHANGE_TABLE_TO_ORDER_ACCOUNT: 'areaTable.canChangeTableToOrderAccount',
  /** Change user assigned to table order account */
  CAN_CHANGE_USER_TO_ORDER_ACCOUNT: 'areaTable.canChangeUserToOrderAccount',
  /** View orders tab */
  CAN_VIEW_ORDERS: 'areaTable.canViewOrders',
  /** View area tables tab */
  CAN_VIEW_AREA_TABLES: 'areaTable.canViewAreaTables',
  /** View cash flow tab */
  CAN_VIEW_CASH_FLOW: 'areaTable.canViewCashFlow',
  /** View orders accounts */
  CAN_VIEW_ORDERS_ACCOUNT: 'areaTable.canViewOrdersAccount',
  /** View settings module */
  CAN_VIEW_SETTINGS: 'areaTable.canViewSettings',
} as const;

/**
 * Point of Sale (POS) permissions
 * @readonly
 */
export const POS_PERMISSIONS = {
  /** Apply discounts */
  APPLY_DISCOUNTS: 'pos.applyDiscounts',
  /** Apply restricted discounts */
  APPLY_RESTRICTED_DISCOUNTS: 'pos.applyRestrictedDiscounts',
  /** Apply gift certificate */
  CAN_APPLY_GIFT_CERTIFICATE: 'pos.canApplyGiftCertificate',
  /** Open/Close cash register */
  CASH_REGISTER_CHANGE_STATE: 'pos.cashRegisterChangeState',
  /** Deposit in cash register */
  CASH_REGISTER_DEPOSIT: 'pos.cashRegisterDeposit',
  /** Tip report */
  CASH_REGISTER_TIP_REPORT: 'pos.cashRegisterTipReport',
  /** Withdraw from cash register */
  CASH_REGISTER_WITHDRAWAL: 'pos.cashRegisterWithdrawal',
  /** Create payment */
  CREATE_PAYMENT: 'pos.createPayment',
  /** Edit payments of closed orders */
  EDIT_PAYMENT_CLOSE_ORDERS: 'pos.editePaymentCloseOrders',
  /** Fingerprint registration */
  FINGERPRINT_REGISTRATION: 'pos.fingerPrintRegistration',
  /** Force checkout time for all users */
  FORCE_CHECKOUT_TIME_ALL_USERS: 'pos.forceCheckoutTimeAllUsers',
  /** Force close orders */
  FORCE_CLOSE_ORDERS: 'pos.forceCloseOrders',
  /** Manage integrations */
  MANAGE_INTEGRATIONS: 'pos.manageIntegrations',
  /** Create orders */
  ORDER_CREATE: 'pos.orderCreate',
  /** Edit order after printing receipt */
  ORDER_EDIT: 'pos.orderEdit',
  /** Re-open order */
  ORDER_RE_OPEN: 'pos.orderReOpen',
  /** Reprint command */
  REPRINT_COMMAND: 'pos.reprintCommand',
  /** Reprint ticket */
  REPRINT_TICKET: 'pos.reprintTicket',
  /** See orders from other users */
  SEE_ORDERS_FROM_OTHER_USERS: 'pos.seeOrdersFromOtherUsers',
  /** Transfer items */
  TRANSFER_ITEMS: 'pos.transferItems',
  /** Update payment */
  UPDATE_PAYMENT: 'pos.updatePayment',
  /** Void items */
  VOID_ITEMS: 'pos.voidItems',
  /** Void orders */
  VOID_ORDERS: 'pos.voidOrders',
  /** Void payment */
  VOID_PAYMENT: 'pos.voidPayment',
  /** Void service charge */
  VOID_SERVICE_CHARGE: 'pos.voidServiceCharge',
} as const;

/**
 * All permissions combined for easy access
 * @readonly
 */
export const ALL_PERMISSIONS = {
  ORGANIZATION: ORGANIZATION_PERMISSIONS,
  ADMIN: ADMIN_PERMISSIONS,
  NOTIFICATION: NOTIFICATION_PERMISSIONS,
  BACK_OFFICE: BACK_OFFICE_PERMISSIONS,
  PAY_TERMINAL: PAY_TERMINAL_PERMISSIONS,
  INVOICING: INVOICING_PERMISSIONS,
  AREA_TABLES: AREA_TABLES_PERMISSIONS,
  POS: POS_PERMISSIONS,
} as const;

/**
 * Type definitions for permissions
 */
export type OrganizationPermission = typeof ORGANIZATION_PERMISSIONS[keyof typeof ORGANIZATION_PERMISSIONS];
export type AdminPermission = typeof ADMIN_PERMISSIONS[keyof typeof ADMIN_PERMISSIONS];
export type NotificationPermission = typeof NOTIFICATION_PERMISSIONS[keyof typeof NOTIFICATION_PERMISSIONS];
export type BackOfficePermission = typeof BACK_OFFICE_PERMISSIONS[keyof typeof BACK_OFFICE_PERMISSIONS];
export type PayTerminalPermission = typeof PAY_TERMINAL_PERMISSIONS[keyof typeof PAY_TERMINAL_PERMISSIONS];
export type InvoicingPermission = typeof INVOICING_PERMISSIONS[keyof typeof INVOICING_PERMISSIONS];
export type AreaTablesPermission = typeof AREA_TABLES_PERMISSIONS[keyof typeof AREA_TABLES_PERMISSIONS];
export type PosPermission = typeof POS_PERMISSIONS[keyof typeof POS_PERMISSIONS];

/**
 * Union type for all permissions
 */
export type Permission =
  | OrganizationPermission
  | AdminPermission
  | NotificationPermission
  | BackOfficePermission
  | PayTerminalPermission
  | InvoicingPermission
  | AreaTablesPermission
  | PosPermission;

/**
 * Permission validation utility
 * @param permission - Permission string to validate
 * @returns {boolean} Whether the permission is valid
 */
export const isValidPermission = (permission: string): permission is Permission => {
  const allPermissionValues = Object.values(ALL_PERMISSIONS).flatMap((group) => Object.values(group));
  return allPermissionValues.includes(permission as Permission);
};
